import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Button,
  TextField,
  Typography,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Accordion } from "components/AccordionForm";
import SwitchField from "components/Forms/SwitchField";
import { isBlank } from "utils/string";
import StatusSelector, { deliveryStatuses } from "./StatusSelector";
import { getDateLimits, lastWeek, now } from "./helpers";
import { DateInputContainer, EmailInputContainer } from "./styled";

import { DELIVERY_STATUS_CUTOFF, Filters } from "pages/EmailFlowLogs/types";
import { EmailFlowLogsSearchRequest } from "@dashboard-v3/api";

dayjs.extend(utc);

type Params = {
  tab: "SEARCH" | "REPORTS";
  disableSubmit: boolean;
  expanded: boolean;
  onExpand: () => void;
  onSubmit: (criteria: EmailFlowLogsSearchRequest) => void;
};

export default function FiltersForm(props: Params) {
  const { t } = useTranslation("emailFlowLogs");
  const history = useHistory();
  const { pathname } = useLocation();
  const [form, setForm] = useState<Filters>(initForm);
  const { tab, disableSubmit, onSubmit, expanded, onExpand } = props;
  function handleOnChange(e) {
    const { name, value, checked } = e.target;

    setForm(previous => {
      if (name === "hasAttachments") {
        return { ...previous, [name]: checked };
      }

      if (name === "status" && value !== "WITH_STORAGE_ERRORS") {
        return { ...previous, [name]: value, errorCodes: [] };
      }

      const updated = { ...previous, [name]: value };

      if (
        name === "fromDate" &&
        value < DELIVERY_STATUS_CUTOFF &&
        deliveryStatuses.includes(previous.status)
      ) {
        updated.status = "";
      }

      return updated;
    });
  }

  function handleErrorCodes(value: Filters["errorCodes"]) {
    setForm(prev => ({ ...prev, errorCodes: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();

    const params = new URLSearchParams();

    Object.entries(form).forEach(([name, value]) => {
      if (Boolean(value) && !isBlank(String(value))) {
        params.append(name, String(value));
      }
    });

    history.push(`${pathname}?${params.toString()}`);

    onSubmit({
      dateFrom: dayjs(`${form.fromDate} ${form.fromTime}`).utc().valueOf(),
      dateTo: dayjs(`${form.toDate} ${form.toTime}`).utc().valueOf(),
      ...(form.sender && { sender: form.sender }),
      ...(form.recipient && { recipient: form.recipient }),
      ...(form.subject && { subject: form.subject }),
      ...(form.messageId && { messageId: form.messageId }),
      ...(form.hasAttachments && { hasAttachments: form.hasAttachments }),
      ...(form.status && { status: form.status }),
      ...(form.errorCodes.length && {
        errorCodes: form.errorCodes,
      }),
    });
  }

  return (
    <Accordion
      style={{ margin: "20px 0" }}
      expanded={expanded}
      onChange={onExpand}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">{t("search.title")}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <EmailInputContainer>
            <TextField
              variant="outlined"
              name="sender"
              label={t("search.sender.label")}
              value={form.sender}
              onChange={handleOnChange}
            />
            <TextField
              variant="outlined"
              name="recipient"
              label={t("search.recipient.label")}
              value={form.recipient}
              onChange={handleOnChange}
            />
            <TextField
              variant="outlined"
              name="subject"
              label={t("search.subject.label")}
              value={form.subject}
              onChange={handleOnChange}
            />
            <TextField
              variant="outlined"
              name="messageId"
              label={t("search.messageId.label")}
              value={form.messageId}
              onChange={handleOnChange}
            />
          </EmailInputContainer>
          <DateInputContainer>
            <TextField
              variant="outlined"
              type="date"
              name="fromDate"
              label={t("common:fromDate")}
              value={form.fromDate}
              onChange={handleOnChange}
              inputProps={{ ...getDateLimits() }}
            />
            <TextField
              variant="outlined"
              type="time"
              name="fromTime"
              label={t("common:time")}
              value={form.fromTime}
              onChange={handleOnChange}
            />
            <TextField
              variant="outlined"
              type="date"
              name="toDate"
              label={t("common:toDate")}
              value={form.toDate}
              onChange={handleOnChange}
              inputProps={{ ...getDateLimits() }}
            />
            <TextField
              variant="outlined"
              type="time"
              name="toTime"
              label={t("common:time")}
              value={form.toTime}
              onChange={handleOnChange}
            />
          </DateInputContainer>
          <StatusSelector
            form={form}
            tab={props.tab}
            onStatusChange={handleOnChange}
            onErrorCodesChange={handleErrorCodes}
          />
          <div style={{ marginBottom: "30px" }}>
            <SwitchField
              name="hasAttachments"
              label={t("search.hasAttachments.label")}
              description={t("search.hasAttachments.desc")}
              value={form.hasAttachments}
              onChange={handleOnChange}
            />
          </div>
          <Button
            type="submit"
            color="primary"
            disableElevation
            variant="contained"
            style={{ alignSelf: "flex-end" }}
            disabled={disableSubmit}
          >
            {tab === "SEARCH" ? t("search.submitBtn") : t("reports.createBtn")}
          </Button>
        </form>
      </AccordionDetails>
    </Accordion>
  );
}

function initForm(): Filters {
  const values: Filters = {
    sender: "",
    recipient: "",
    subject: "",
    messageId: "",
    fromDate: lastWeek(),
    fromTime: "00:00",
    toDate: now(),
    toTime: "23:59",
    hasAttachments: false,
    errorCodes: [],
    status: "",
  };

  if (window.location.search) {
    const params = new URLSearchParams(window.location.search);
    params.forEach((value, key) => {
      if (key === "errorCodes") {
        const errors = value.split(",") as Filters["errorCodes"];
        values[key] = errors || [];
      } else if (key === "hasAttachments") {
        values[key] = value === "true";
      } else {
        values[key] = value;
      }
    });
  }

  return values;
}
